"use client";

import { useRouter } from "next/navigation";
import { useCallback, useEffect, useId, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { api, mediaUrl } from "@/lib/api";
import type { ProductShort } from "@/lib/api-types";
import { formatPrice } from "@/lib/utils";

const DEBOUNCE_MS = 250;
const MIN_LENGTH = 2;
const MAX_SUGGESTIONS = 6;

export function SearchInput() {
  const router = useRouter();
  const [q, setQ] = useState("");
  const [items, setItems] = useState<ProductShort[]>([]);
  const [total, setTotal] = useState(0);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const inputId = useId();
  const listboxId = `${inputId}-listbox`;
  const rootRef = useRef<HTMLFormElement>(null);
  const reqIdRef = useRef(0);

  // Закрывать при клике вне.
  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  // Debounced fetch.
  useEffect(() => {
    const trimmed = q.trim();
    if (trimmed.length < MIN_LENGTH) {
      setItems([]);
      setTotal(0);
      setLoading(false);
      return;
    }
    const myReq = ++reqIdRef.current;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        // /suggest вместо /search — быстрее (нет ts_rank/FTS), отдаёт до 8 топ-матчей.
        const items = await api.searchSuggest(trimmed, MAX_SUGGESTIONS);
        // Игнорируем устаревшие ответы.
        if (myReq !== reqIdRef.current) return;
        setItems(items);
        setTotal(items.length);
        setOpen(true);
        setHighlight(-1);
      } catch {
        if (myReq !== reqIdRef.current) return;
        setItems([]);
        setTotal(0);
      } finally {
        if (myReq === reqIdRef.current) setLoading(false);
      }
    }, DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [q]);

  const submitFull = useCallback(() => {
    const trimmed = q.trim();
    if (!trimmed) return;
    router.push(`/search?q=${encodeURIComponent(trimmed)}`);
    setOpen(false);
  }, [q, router]);

  const goTo = useCallback(
    (p: ProductShort) => {
      router.push(`/catalog/${p.canonical_slug}`);
      setOpen(false);
      setQ("");
    },
    [router],
  );

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open || items.length === 0) {
      if (e.key === "Enter") {
        e.preventDefault();
        submitFull();
      }
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((i) => (i + 1) % items.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((i) => (i <= 0 ? items.length - 1 : i - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (highlight >= 0 && items[highlight]) goTo(items[highlight]);
      else submitFull();
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const showDropdown = open && q.trim().length >= MIN_LENGTH;

  return (
    <form
      ref={rootRef}
      role="search"
      onSubmit={(e) => {
        e.preventDefault();
        submitFull();
      }}
      className="relative hidden flex-1 md:block"
    >
      <Input
        type="search"
        name="q"
        placeholder="Поиск: артикул или название…"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onFocus={() => q.trim().length >= MIN_LENGTH && setOpen(true)}
        onKeyDown={onKeyDown}
        aria-label="Поиск по каталогу"
        aria-autocomplete="list"
        aria-expanded={showDropdown}
        aria-controls={listboxId}
        aria-activedescendant={
          highlight >= 0 && items[highlight] ? `${listboxId}-${items[highlight].id}` : undefined
        }
      />

      {showDropdown && (
        <div
          id={listboxId}
          role="listbox"
          aria-label="Подсказки поиска"
          className="absolute inset-x-0 top-full z-30 mt-1 max-h-[60vh] overflow-y-auto rounded-[8px] border border-border bg-white shadow-lg"
        >
          {loading && items.length === 0 ? (
            <p className="px-4 py-3 text-sm text-text-muted">Ищем…</p>
          ) : items.length === 0 ? (
            <p className="px-4 py-3 text-sm text-text-muted">
              Ничего не нашлось. Нажмите Enter для&nbsp;полного поиска.
            </p>
          ) : (
            <ul className="py-1">
              {items.map((p, idx) => {
                const img = mediaUrl(p.primary_image) ?? "/placeholders/no-photo.jpg";
                const active = idx === highlight;
                return (
                  <li
                    key={p.id}
                    id={`${listboxId}-${p.id}`}
                    role="option"
                    aria-selected={active}
                  >
                    <button
                      type="button"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        goTo(p);
                      }}
                      onMouseEnter={() => setHighlight(idx)}
                      className={
                        "flex w-full items-center gap-3 px-3 py-2 text-left transition-colors " +
                        (active ? "bg-bg-muted" : "hover:bg-bg-muted")
                      }
                    >
                      {/* eslint-disable-next-line @next/next/no-img-element */}
                      <img
                        src={img}
                        alt=""
                        loading="lazy"
                        className="h-12 w-12 shrink-0 rounded-[6px] bg-bg-muted object-contain"
                      />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium leading-tight">{p.name}</p>
                        <p className="mt-0.5 text-xs text-text-subtle">арт. {p.external_id}</p>
                      </div>
                      <span className="tabular text-sm font-semibold whitespace-nowrap">
                        {formatPrice(p.price)}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          {items.length > 0 && total > items.length && (
            <div className="border-t border-border bg-bg-muted px-3 py-2 text-center">
              <button
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  submitFull();
                }}
                className="text-sm font-medium text-brand-700 hover:underline"
              >
                Показать все результаты ({total})
              </button>
            </div>
          )}
        </div>
      )}
    </form>
  );
}
